import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import missionQuiz from "../data/missionQuiz";
import { saveResultInsight } from "../utils/insights";
import "./TestApp.css";

const PROFILE_CHECKPOINT = 12;
const isNativeApp = Capacitor.isNativePlatform();
const initialProfile = { age: "", purpose: "", region: "", source: "", consent: false };
const initialScore = { e: 0, i: 0, s: 0, n: 0, f: 0, t: 0, p: 0, j: 0 };

const shuffle = (items) => {
  const copy = [...items];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [copy[index], copy[randomIndex]] = [copy[randomIndex], copy[index]];
  }
  return copy;
};

const storyStages = [
  [0],             // the crash landing always opens the story
  [1, 2],          // assess Earth and the damaged return system
  [3],             // the first Earthling appears before conversation
  [4, 5],          // first-contact exchange and conversation preparation
  [6, 7],          // building trust
  [12, 13],        // deepen the Earth exploration
  [14, 15],        // understand human choices / core checkpoint
  [20],            // detect the unknown signal
  [21, 22, 23],    // analyze and investigate the signal
  [24],            // Earth proposes an alliance
  [25, 26, 27],    // shape the alliance
  [8],             // discover repair technology
  [9, 10],         // repair and make the return plan
  [18, 19],        // final decision before departure
  [16, 17],        // choose and prepare Earth records
  [11],            // farewell
  [28],            // present the mission findings
  [29, 30, 31],    // final archive and next mission
];

const chapterForIndex = (index, lang) => {
  const groups = [
    [[0, 1, 2], ["1. 불시착", "1. CRASH LANDING"]],
    [[3, 4, 5], ["2. 첫 조우", "2. FIRST CONTACT"]],
    [[6, 7], ["3. 신뢰 형성", "3. BUILDING TRUST"]],
    [[12, 13], ["4. 지구 탐사", "4. EARTH EXPLORATION"]],
    [[14, 15], ["5. 지구인의 선택", "5. HUMAN CHOICES"]],
    [[20, 21, 22, 23], ["6. 미지의 신호", "6. UNKNOWN SIGNAL"]],
    [[24, 25, 26, 27], ["7. 지구 동맹", "7. EARTH ALLIANCE"]],
    [[8, 9, 10], ["8. 귀환 준비", "8. RETURN PLAN"]],
    [[18, 19], ["9. 최종 결정", "9. FINAL DECISION"]],
    [[16, 17], ["10. 지구의 기억", "10. EARTH MEMORY"]],
    [[11], ["11. 작별", "11. FAREWELL"]],
    [[28, 29, 30, 31], ["12. 최종 기록", "12. FINAL ARCHIVE"]],
  ];
  const match = groups.find(([indexes]) => indexes.includes(index));
  return match ? match[1][lang === "ko" ? 0 : 1] : "";
};
const createQuizOrder = () => storyStages.flatMap((stage) => shuffle(stage));

const Progress = ({ index, maxIndex }) => {
  const percent = Math.min(100, ((index + 1) / maxIndex) * 100);
  return <div className="progress-wrap" aria-label={`${index + 1} / ${maxIndex}`}><div className="progress-meta"><span>MISSION SCAN</span><strong>{index + 1} / {maxIndex}</strong></div><div className="progress-container"><div className="indicator" style={{ width: `${percent}%` }} /></div></div>;
};

const Test = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const resumeFullScan = isNativeApp && new URLSearchParams(window.location.search).get("continue") === "1";
  const savedScore = (() => { try { return JSON.parse(localStorage.getItem("cooldoc_scores")) || initialScore; } catch (_) { return initialScore; } })();
  const [quizIndex, setQuizIndex] = useState(resumeFullScan ? PROFILE_CHECKPOINT : 0);
  const [showProfile, setShowProfile] = useState(false);
  const [lang, setLang] = useState("en");
  const [name, setName] = useState("");
  const [profile, setProfile] = useState(initialProfile);
  const [score, setScore] = useState(resumeFullScan ? savedScore : initialScore);
  const [quizOrder] = useState(() => {
    if (resumeFullScan) {
      try {
        const savedOrder = JSON.parse(localStorage.getItem("cooldoc_quiz_order_v3"));
        if (Array.isArray(savedOrder) && savedOrder.length === missionQuiz.length) return savedOrder;
      } catch (_) {}
    }
    const newOrder = createQuizOrder();
    localStorage.setItem("cooldoc_quiz_order_v3", JSON.stringify(newOrder));
    return newOrder;
  });

  useEffect(() => { setLang(localStorage.getItem("cooldoc_lang") === "ko" ? "ko" : "en"); }, []);
  const getResult = () => {
    let core = initialScore;
    try { core = JSON.parse(localStorage.getItem("cooldoc_core_scores")) || initialScore; } catch (_) {}
    const pick = (left, right) => score[left] === score[right]
      ? (core[left] > core[right] ? left : right)
      : (score[left] > score[right] ? left : right);
    return `${pick("e", "i")}${pick("s", "n")}${pick("f", "t")}${pick("p", "j")}`;
  };

  useEffect(() => {
    if (quizIndex < missionQuiz.length) return undefined;
    setLoading(true);
    const resultType = getResult();
    localStorage.setItem("cooldoc_scores", JSON.stringify(score));
    localStorage.setItem("cooldoc_result_depth", "full");
    saveResultInsight(resultType, score);
    const timer = window.setTimeout(() => history.push(`/result/${resultType}`), 1800);
    return () => window.clearTimeout(timer);
  }, [quizIndex, history]);

  const selectAnswer = (selected) => {
    if (!selected || loading) return;
    setScore((previous) => ({ ...previous, [selected.type]: previous[selected.type] + 1 }));
    if (quizIndex + 1 === PROFILE_CHECKPOINT) { setShowProfile(true); return; }
    setQuizIndex((current) => current + 1);
  };

  const viewQuickResult = () => {
    const resultType = getResult();
    localStorage.setItem("cooldoc_scores", JSON.stringify(score));
    localStorage.setItem("cooldoc_core_scores", JSON.stringify(score));
    localStorage.setItem("cooldoc_result_depth", "basic");
    history.push(`/result/${resultType}`);
  };
  const continueMission = () => {
    setShowProfile(false);
    setQuizIndex(PROFILE_CHECKPOINT);
  };

  const installAndroid = () => {
    const installButton = document.querySelector(".install-dock button");
    if (installButton) installButton.click();
    else window.alert(lang === "ko" ? "Chrome 메뉴에서 '앱 설치' 또는 '홈 화면에 추가'를 선택해주세요." : "In Chrome, choose Install app or Add to Home screen.");
  };

  const installIphone = () => {
    window.alert(lang === "ko" ? "Safari 아래의 공유 버튼을 누른 뒤 '홈 화면에 추가'를 선택해주세요." : "In Safari, tap Share, then Add to Home Screen.");
  };

  if (loading || quizIndex >= missionQuiz.length) return <div id="top-container" className="question"><div id="loading-container" role="status" aria-live="polite"><div className="scan-loader"><span /><span /><span /></div><div className="loading-text">{lang === "ko" ? "\uc9c0\uad6c\uc778 \uc815\uccb4\uc131 \ubd84\uc11d \uc911..." : "Analyzing Earthling identity..."}</div></div></div>;

  if (showProfile) {
    return <div id="top-container" className="reply"><main className="container reply profile-checkpoint mission-checkpoint">
      <div className="checkpoint-badge">CHECKPOINT 01</div>
      <div className="checkpoint-panel">
        <img src="/image/logo-3d.png" alt="COOLDOC" />
        <h2>{lang === "ko" ? "\uae30\ubcf8 \ud0d0\uc0ac 12\ubb38\ud56d \uc644\ub8cc" : "12 basic questions complete"}</h2>
        <p>{lang === "ko" ? "코어 결과가 준비됐습니다. 앱을 설치해 간단 결과를 확인하고, 전체 분석을 선택하면 심화 탐사 20문항이 이어집니다." : "Your core result is ready. Install the app to view it, then unlock Full Analysis to continue with 20 deep-scan questions."}</p>
        <div className="checkpoint-progress"><span>12 / 32</span><div><i /></div><strong>{lang === "ko" ? "20\ubb38\ud56d \ub0a8\uc74c" : "20 questions remaining"}</strong></div>
      </div>
      {isNativeApp ? <button className="cta" onClick={viewQuickResult}>{lang === "ko" ? "12문항 간단 결과 보기" : "View 12-question result"}</button> : <>
        <div className="install-first-title">{lang === "ko" ? "결과를 보기 전에 앱을 먼저 설치하세요" : "Install the app before viewing your result"}</div>
        <div className="checkpoint-install-options"><button className="checkpoint-install android primary-install" onClick={installAndroid}>Android {lang === "ko" ? "앱 설치" : "Install"}</button><button className="checkpoint-install iphone" onClick={installIphone}>iPhone {lang === "ko" ? "앱 설치" : "Install"}</button></div>
        <button className="checkpoint-result-link" onClick={viewQuickResult}>{lang === "ko" ? "설치했어요 · 간단 결과 보기" : "Installed · View quick result"}</button>
      </>}
      <p className="checkpoint-note">{lang === "ko" ? (isNativeApp ? "간단 결과를 확인한 뒤 전체 분석을 선택하면 심화 탐사 20문항이 이어집니다." : "앱에서 결과를 저장하고, 전체 분석을 구매하면 심화 탐사 20문항을 이어갈 수 있습니다.") : (isNativeApp ? "After your quick result, unlock the full analysis to continue with 20 deep-scan questions." : "Save your result in the app, then unlock the full analysis to continue with 20 questions.")}</p>
    </main></div>;
  }

  const currentIndex = quizOrder[quizIndex];
  const current = missionQuiz[currentIndex];
  return <div id="top-container" className="question"><main className="container test"><img className="logo" src="/image/logo-3d.png" alt="COOLDOC" /><Progress index={quizIndex} maxIndex={missionQuiz.length} /><img src="/image/doc_pointing.png" className="point-down" alt="COOLDOC guide" /><section className="quiz-card" aria-live="polite"><div className="q-title"><div className="chapter-label">{chapterForIndex(currentIndex, lang)}</div><div>Q{quizIndex + 1}. {current.q[lang]}</div></div><div className="answer-list">{current.a.map((item,index)=><button className="answer" onClick={()=>selectAnswer(item)} key={`${quizIndex}-${index}`}>{item.txt[lang]}</button>)}</div></section></main></div>;
};

export default Test;











